import {useDuringFirstRender} from '@sail/react';
import * as React from 'react';
import makeMain from 'src/internal/errors/utils/makeMain';
import Analytics from 'src/public/analytics';
import Metrics from 'src/public/metrics';
import Reports from 'src/public/reports';
import useObservabilityConfig from 'src/public/useObservabilityConfig';

import type {IObservabilityApi} from 'src/internal/types';

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const ObservabilityApiContext =
  React.createContext<IObservabilityApi | null>(null);

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export function useObservabilityApi(): IObservabilityApi | null {
  return React.useContext(ObservabilityApiContext);
}

type Props = {
  children: React.ReactNode;
  updateMainHubOnMount?: boolean;
};

export default function ObservabilityApiProvider({
  children,
  updateMainHubOnMount = false,
}: Props): React.ReactElement {
  const parentObservabilityApi = useObservabilityApi();
  const config = useObservabilityConfig();

  const value = React.useMemo(() => {
    if (!config) {
      return null;
    }

    return {
      analytics: new Analytics(config),
      metrics: new Metrics(config),
      reports: new Reports(config),
    };
  }, [config]);

  useDuringFirstRender(() => {
    // Only root ObservabilityApiProvider should update main hub on mount.
    // If there is a parent ObservabilityApiProvider, it will handle updating the main hub.
    if (updateMainHubOnMount && !parentObservabilityApi) {
      const mainReportsHub = value?.reports.getMainHub();
      if (mainReportsHub) {
        makeMain(mainReportsHub);
      }
    }
  });

  return (
    <ObservabilityApiContext.Provider value={value}>
      {children}
    </ObservabilityApiContext.Provider>
  );
}
